import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import isEqual from 'lodash.isequal';
import { useTranslation } from 'react-i18next';

// Styled Components
import {
  Container,
  RowContainer,
  Title,
  SmallTitle,
  ColumnsContainer,
  Column,
  RowSubContainerBottom,
  ContainerButtons,
  Tag,
  ColumnTitleContainer,
  RowSubContainerLeft,
  RowSubContainerRight,
  RowContainerLast,
  ColumnsContainerDouble,
  TitleTagContainer,
  InputTextAreaStyled,
  ChangeStateTag,
  ChangeStateTagContainer,
  StateTag,
} from './styles';

// Media
import { useIsLandscapeTablet } from '../../../hooks/useMediaBreakPoints';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';

// Components
import InputRoid from '../../inputs/InputRoid';
import { Definition } from '../Definition';
import { MainButton } from '../../inputs/buttons/MainButton';

// Types
import { ContentCardResumeProps, marginArrow, directionArrow } from './types';
import { CommunicationSendStatus } from '../../../types/communication';
import { UNSPECIFIC } from '../../../types/utils';

// Action
import { getCurrentIndexTabFormByTab } from '../../../state/selectors/ui/tabs';
import { tabsType } from '../../../state/reducers/ui/tabs';
import { variant } from '../SpecialTag/types';

import { openModal } from '../../../state/actions/ui/modal';
import { modalIds } from '../../modals/SuperModal/types';
import { useAuth } from '../../../hooks/useAuth';
import { Role } from '../../../types/auth';
import { OrderState } from '../../../types/orders';
import { canRoleChangeStateOf } from '../../../utils/rolePermissions';

// Helper: Map state string to SpecialTag variant for colored pill
const getStateVariant = (state?: string): variant | null => {
  if (!state) return null;
  const stateVariantMap: Record<string, variant> = {
    Open: variant.Open,
    open: variant.Open,
    Managed: variant.Managed,
    managed: variant.Managed,
    'pending-work': variant.PendingWork,
    PendingWork: variant.PendingWork,
    'work-in-progress': variant.WorkInProgress,
    WorkInProgress: variant.WorkInProgress,
    // Estados propios del Recorregut de Vigilància (2026-08-05). Sin estas
    // entradas el mapa devolvía `null` y la pill salía gris y sin texto — y
    // esta misma card la usa el resumen del recorrido.
    'in-progress': variant.InProgress,
    paused: variant.Paused,
    'pending-validation': variant.PendingValidation,
    PendingValidation: variant.PendingValidation,
    'pending-at-validation': variant.PendingATValidation,
    PendingATValidation: variant.PendingATValidation,
    Annulled: variant.Annulled,
    annulled: variant.Annulled,
    End: variant.End,
    end: variant.End,
  };
  return stateVariantMap[state] || null;
};

export default function ContentCardResume({
  title,
  roid,
  operator,
  data,
  hour,
  groupInter,
  media,
  sendStatus,
  capitol,
  subCapitol,
  classification,
  predefine,
  team,
  concession,
  road,
  pkInit,
  pkEnd,
  track,
  city,
  roadDirections,
  direction,
  margin,
  lane,
  leftButton,
  rightButton,
  tag,
  tagContent,
  description,
  notes,
  onEditCommunication,
  onSaveCommunication,
  storedDataOTSelected,
  changeStateTag,
  state,
  revalidatorFunction,
  orderId,
  communicationId,
  disabled,
  orderType,
  ...props
}: ContentCardResumeProps): JSX.Element {
  const { t } = useTranslation('forms');
  const isLandscape = useIsLandscapeTablet();
  // Feature flag para mostrar/ocultar campo margen
  const showMargin = useFeatureFlag('showMargin', true);

  const selectorIndexTabForm = useSelector(
    getCurrentIndexTabFormByTab(tabsType.COMMUNICATION),
    isEqual
  );
  const dispatch = useDispatch();
  const { currentRole } = useAuth();

  /**
   * Gate del gesto: no basta con que el rol toque este TIPO de entidad — tiene
   * que haber algún destino desde el estado actual. Con
   * `canRoleChangeEntityState()` un Tablet veía la píldora «Cambiar estado» en
   * una actuación y el modal se abría vacío.
   */
  const canChangeState = canRoleChangeStateOf(
    currentRole as Role,
    orderType ?? '',
    state as OrderState,
  );

  const openModalChangeState = () => {
    if (!canChangeState) return;
    dispatch(
      openModal(modalIds.CHANGE_STATE_MODAL, {
        title: t('workOrder.changeStateConfirm'),
        titleSaveModal: t('modals:changeState.confirmMessage'),
        indexExpedition: Number(
          typeof roid === 'string'
            ? roid.split('.')[1]
            : Array.isArray(roid)
              ? roid[1]
              : '0'
        ),
        revalidatorFunction,
        currentState: state,
        orderId: orderId,
        communicationId: communicationId,
        orderType: orderType ?? undefined,
      })
    );
  };

  return (
    <Container {...props}>
      <TitleTagContainer>
        <Title size="L" variant="semiBold" title={title}>
          {title}
        </Title>
        {tag && <Tag variant={tag} title={tagContent} />}
        {/* Botón de cambio de estado con dos píldoras: "Cambiar estado" + estado con color */}
        {orderType && changeStateTag && canChangeState && (
          <ChangeStateTagContainer onClick={openModalChangeState}>
            <ChangeStateTag
              variant={variant.Default}
              title={t('common:buttons.changeState')}
            />
            {state && getStateVariant(state) && (
              <StateTag variant={getStateVariant(state) as variant} />
            )}
          </ChangeStateTagContainer>
        )}
      </TitleTagContainer>
      <RowContainer>
        <InputRoid
          label="ROID"
          roid={
            typeof roid === 'string'
              ? roid.split('.')
              : Array.isArray(roid)
                ? roid
                : []
          }
          disabled
          onChangeInputRoid={() => {
            null;
          }}
        />
      </RowContainer>
      <RowContainer>
        <RowSubContainerLeft>
          <ColumnTitleContainer>
            <SmallTitle size={isLandscape ? 'XS' : 'M'} variant="semiBold">
              {t('communication.basicInfo')}
            </SmallTitle>
            <ColumnsContainerDouble>
              <Column>
                <Definition
                  title={t('communication.operator')}
                  value={operator}
                />
                <Definition
                  title={t('communication.registerDate')}
                  value={data}
                />
                <Definition
                  title={t('communication.registerHour')}
                  value={hour}
                />
              </Column>
              <Column>
                {groupInter && (
                  <Definition
                    title={t('communication.interlocutorGroup')}
                    value={groupInter}
                  />
                )}
                {media && (
                  <Definition title={t('communication.media')} value={media} />
                )}
                {sendStatus !== undefined && (
                  <Definition
                    title={t('workOrder.sentReceived')}
                    value={
                      CommunicationSendStatus.SENT === sendStatus
                        ? t('common:communication.sent')
                        : t('common:communication.received')
                    }
                  />
                )}
              </Column>
            </ColumnsContainerDouble>
          </ColumnTitleContainer>
        </RowSubContainerLeft>
        <RowSubContainerRight>
          <ColumnTitleContainer>
            <SmallTitle size={isLandscape ? 'XS' : 'M'} variant="semiBold">
              {t('common:workOrder.assignmentTitle')}
            </SmallTitle>

            <Column>
              <Definition
                title={t('workOrder.chapter')}
                value={capitol || storedDataOTSelected?.capitol || ''}
              />
              <Definition
                title={t('workOrder.subchapter')}
                value={subCapitol || storedDataOTSelected?.subCapitol || ''}
              />
              <Definition
                title={t('workOrder.classification')}
                value={classification || storedDataOTSelected?.classification || ''}
              />
              {predefine && (
                <Definition
                  title={t('workOrder.predefined')}
                  value={predefine}
                />
              )}
              {team && selectorIndexTabForm === 0 && (
                <Definition title={t('workOrder.team')} value={team} />
              )}
            </Column>
          </ColumnTitleContainer>
        </RowSubContainerRight>
      </RowContainer>
      <RowSubContainerBottom>
        <ColumnTitleContainer>
          <SmallTitle size={isLandscape ? 'XS' : 'M'} variant="semiBold">
            {t('common:workOrder.descriptionTitle')}
          </SmallTitle>
          <ColumnsContainer>
            <Column>
              <Definition title={t('user.concession')} value={concession} />
              <Definition title={t('workOrder.road')} value={road} />
              <Definition title={t('workOrder.pkInitial')} value={pkInit} />
              <Definition title={t('workOrder.pkFinal')} value={pkEnd} />
            </Column>
            <Column>
              <Definition title={t('workOrder.roadType')} value={track} />
              {direction && direction !== UNSPECIFIC && (
                <Definition
                  title={t('workOrder.direction')}
                  value={`${direction === 'both' || !roadDirections[0]
                      ? city
                      : direction === 'down'
                        ? roadDirections[0]
                        : roadDirections[roadDirections.length - 1]
                    } ${directionArrow[direction]}`}
                />
              )}
              {showMargin && margin && margin !== UNSPECIFIC && (
                <Definition
                  title={t('workOrder.margin')}
                  value={marginArrow[margin]}
                />
              )}
              <Definition title={t('workOrder.lane')} value={lane} />
            </Column>
          </ColumnsContainer>
        </ColumnTitleContainer>
      </RowSubContainerBottom>
      <RowContainerLast>
        <InputTextAreaStyled
          label={t('workOrder.description')}
          disabled
          value={description}
        />
        {notes && (
          <InputTextAreaStyled
            label={t('workOrder.notes')}
            disabled
            value={notes}
          />
        )}
        <ContainerButtons onSubmit={onSaveCommunication}>
          {leftButton && (
            <MainButton
              type="button"
              variant="secondary"
              text={leftButton}
              onClick={onEditCommunication}
              disabled={disabled}
            />
          )}
          {rightButton && <MainButton type="submit" text={rightButton} />}
        </ContainerButtons>
      </RowContainerLast>
    </Container>
  );
}
